import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useNotifications } from 'modules/notification';
import { useLazyQuery, useMutation } from '@apollo/client';
import { ProposalSnapshotResponse, ProposalSnapshotVariables } from '../api';
import {
  UPDATE_PROPOSAL,
  GET_PROPOSAL_SNAPSHOT,
  CREATE_PROPOSAL,
  CREATE_PROPOSAL_ON_SUBMISSION_APPROVAL,
  CreateProposalDTO,
} from '../api';

export function useProposal() {
  const { showNotification } = useNotifications();

  const [getSnapshot] = useLazyQuery<ProposalSnapshotResponse, ProposalSnapshotVariables>(GET_PROPOSAL_SNAPSHOT);

  const [createProposal, { loading: isProposalCreating }] = useMutation(CREATE_PROPOSAL);
  const [createProposalOnSubmissionApproval, { loading: isProposalCreatingOnSubmissionApproval }] = useMutation(
    CREATE_PROPOSAL_ON_SUBMISSION_APPROVAL
  );

  const handleCreateProposal = useCallback(
    async (dto: CreateProposalDTO) => {
      try {
        await createProposal({ variables: { input: dto } });

        showNotification('Proposal successfully created!');
      } catch (error) {
        logError(error);
        showNotification('Something went wrong', { type: 'error' });
      }
    },
    [showNotification, createProposal]
  );

  const handleCreateProposalOnSubmissionApproval = useCallback(
    async (dto: CreateProposalDTO, notification?: { recipient: string; internalEmails: string[] }) => {
      try {
        await createProposalOnSubmissionApproval({
          variables: { input: { proposal: dto, ...(notification ? { notification } : {}) } },
        });

        showNotification('Proposal successfully created!');
      } catch (error) {
        logError(error);
        showNotification('Something went wrong', { type: 'error' });
      }
    },
    [createProposalOnSubmissionApproval, showNotification]
  );

  const [updateProposal, { loading: isProposalUpdating }] = useMutation(UPDATE_PROPOSAL, {
    context: { authRequired: true },
    refetchQueries: ['Proposal'],
  });

  const handleUpdateProposal = useCallback(
    async (id: string, fieldsObj: Record<string, any>) => {
      try {
        await updateProposal({ variables: { id, fieldsObj } });

        showNotification('Status updated!');
      } catch (error) {
        logError(error);
        showNotification('Status not updated!', { type: 'error' });
      }
    },
    [showNotification, updateProposal]
  );

  const handleGetProposalSnapshot = useCallback(
    async (proposalId: string) => {
      const { data } = await getSnapshot({ variables: { id: proposalId } });
      return data?.proposal?.snapshot ?? null;
    },
    [getSnapshot]
  );

  return {
    isProposalUpdating,
    handleUpdateProposal,
    handleCreateProposal,
    handleGetProposalSnapshot,
    handleCreateProposalOnSubmissionApproval,
    isProposalCreating: isProposalCreating || isProposalCreatingOnSubmissionApproval,
  };
}
